import { Capacitor } from '@capacitor/core';

const bridge = () => (window as any).NativeBridge;
const isExternal = () => !Capacitor.isPluginAvailable('SecureStoragePlugin');

declare global {
  interface Window {
    _onAppStateChange?: (isActive: boolean) => void;
    _onBackButton?: (canGoBack: boolean) => void;
  }
}

export const secureStorageSet = async ({ key, value }: { key: string; value: string }): Promise<void> => {
  const isAndroid = Capacitor.getPlatform() === 'android';

  if (isExternal() || isAndroid) {
    try {
      bridge().setSecureStorage(key, value);
    } catch (error) {
      console.error('secure storage set이 실패');
    }
  } else {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    await SecureStoragePlugin.set({ key, value });
  }
};

export const secureStorageGet = async ({ key }: { key: string }): Promise<string> => {
  const isAndroid = Capacitor.getPlatform() === 'android';

  if (isExternal() || isAndroid) {
    const value = bridge().getSecureStorage(key);
    if (!value) throw new Error(`secure storage 값이 null 입니다. key: ${key}`);

    return value;
  } else {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    const { value } = await SecureStoragePlugin.get({ key });
    if (!value) throw new Error(`secure storage 값이 null 입니다. key: ${key}`);

    return value;
  }
};

export const secureStorageRemove = async ({ key }: { key: string }): Promise<void> => {
  const isAndroid = Capacitor.getPlatform() === 'android';

  if (isExternal() || isAndroid) {
    bridge().removeSecureStorage(key);
  } else {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    await SecureStoragePlugin.remove({ key });
  }
};

export const addAppStateChangeListener = async (callback: (isActive: boolean) => void): Promise<void> => {
  if (isExternal()) {
    window._onAppStateChange = callback;
  } else {
    const { App } = await import('@capacitor/app');
    await App.addListener('appStateChange', ({ isActive }) => callback(isActive));
  }
};

export const removeAppStateChangeListener = () => {
  window._onAppStateChange = undefined;
};

export const deviceGetLanguageCode = async () => {
  if (isExternal()) {
    return bridge().getLanguageCode();
  } else {
    const { Device } = await import('@capacitor/device');
    const languageCode = await Device.getLanguageCode();
    return languageCode.value;
  }
};

export const exitApp = async (): Promise<void> => {
  if (isExternal()) {
    bridge().exitApp();
  } else {
    const { App } = await import('@capacitor/app');
    App.exitApp();
  }
};

export const addBackButtonListener = async (callback: (canGoBack: boolean) => void): Promise<void> => {
  if (isExternal()) {
    window._onBackButton = callback;
  } else {
    const { App } = await import('@capacitor/app');
    await App.addListener('backButton', ({ canGoBack }) => callback(canGoBack));
  }
};

export const removeBackButtonListener = () => {
  window._onBackButton = undefined;
};

export const navigateInApp = (url: string) => {
  if (bridge()) {
    bridge().loadUrl(url);
  } else {
    window.location.href = url;
  }
};
